/**
 * LoadingSkeleton.tsx — Primitiva E1 de estado de carga.
 *
 * Patrón: CVA + tokens cumple-* (ADR-025 + SH-1/ADR-029).
 * Referencia canónica: Button.tsx.
 *
 * Variantes:
 *   - card (default): cards apiladas — carga inicial de página/assessment.
 *   - list: filas tipo lista 72 px con badge + texto + acción — hallazgos/clientes.
 *   - block: rectángulo único — upload de evidencia, schema de formulario.
 *
 * WCAG:
 *   role="status" + aria-busy="true" anuncian el estado de carga a AT.
 *   aria-label describe el contenido esperado (configurable via prop label).
 *   Las celdas de esqueleto llevan aria-hidden="true" para que AT no las lea.
 *   prefers-reduced-motion: la animación se desactiva vía clase Tailwind motion-safe.
 *
 * API compatible con el legacy LoadingSkeleton de ui.tsx:
 *   - rows?: number (default 3) — conservado para import-swap sin cambios en consumidores.
 *   - variant?: 'card' | 'list' | 'block' — nuevo.
 *   - label?: string — nuevo (aria-label).
 *
 * El legacy ui.tsx NO se borra (migración progresiva — DEUDA-V2-UI ADR-025).
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes CVA
// ---------------------------------------------------------------------------

const loadingSkeletonVariants = cva(
  // Base: role/aria se aplican en el componente, no en CVA
  [],
  {
    variants: {
      variant: {
        card: '',
        list: '',
        block: '',
      },
    },
    defaultVariants: {
      variant: 'card',
    },
  },
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LoadingSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof loadingSkeletonVariants> {
  /** Número de filas/ítems placeholder. Default 3. */
  rows?: number;
  /** Texto descriptivo para aria-label (anunciado por AT). Default "Cargando". */
  label?: string;
}

// ---------------------------------------------------------------------------
// Sub-renders por variante (internos)
// ---------------------------------------------------------------------------

/** Card skeleton: card con header + body (carga de página/assessment) */
function CardRows({ rows }: { rows: number }): JSX.Element {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="rounded-2xl border border-cumple-gray-200 bg-white p-4 space-y-3"
        >
          {/* Header simulado */}
          <div className="flex items-center gap-3">
            <div className="h-4 w-24 rounded bg-cumple-gray-200 motion-safe:animate-pulse" />
            <div className="h-3 w-16 rounded bg-cumple-gray-100 motion-safe:animate-pulse" />
          </div>
          {/* Body simulado */}
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-cumple-gray-200 motion-safe:animate-pulse" />
            <div className="h-3 w-3/4 rounded bg-cumple-gray-100 motion-safe:animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** List skeleton: filas 72 px con badge + texto + botón acción (hallazgos/clientes) */
function ListRows({ rows }: { rows: number }): JSX.Element {
  return (
    <ul className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          aria-hidden="true"
          className="flex items-center gap-3 min-h-[72px] rounded-2xl border border-cumple-gray-200 bg-white p-4"
        >
          {/* Badge placeholder */}
          <div className="h-6 w-16 rounded-full bg-cumple-gray-200 motion-safe:animate-pulse shrink-0" />
          {/* Texto */}
          <div className="flex-1 space-y-2">
            <div className="h-4 w-2/3 rounded bg-cumple-gray-200 motion-safe:animate-pulse" />
            <div className="h-3 w-1/3 rounded bg-cumple-gray-100 motion-safe:animate-pulse" />
          </div>
          {/* Botón acción */}
          <div className="h-9 w-12 rounded-lg bg-cumple-gray-100 motion-safe:animate-pulse shrink-0" />
        </li>
      ))}
    </ul>
  );
}

/** Block skeleton: rectángulo simple (upload evidencia, schema formulario) */
function BlockRows({ rows }: { rows: number }): JSX.Element {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="h-20 w-full rounded-xl border border-cumple-gray-200 bg-cumple-gray-100 motion-safe:animate-pulse"
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * LoadingSkeleton — Indicador de carga con skeleton placeholder.
 *
 * role="status" + aria-busy="true" + aria-label anuncian a AT que el contenido
 * está cargando. Las celdas internas llevan aria-hidden para no duplicar info.
 *
 * Usar `label` para describir el contenido esperado:
 *   <LoadingSkeleton label="Cargando hallazgos…" />
 */
const LoadingSkeleton = React.forwardRef<HTMLDivElement, LoadingSkeletonProps>(
  ({ className, variant = 'card', rows = 3, label = 'Cargando', ...props }, ref) => {
    const content =
      variant === 'list' ? (
        <ListRows rows={rows} />
      ) : variant === 'block' ? (
        <BlockRows rows={rows} />
      ) : (
        <CardRows rows={rows} />
      );

    return (
      <div
        ref={ref}
        role="status"
        aria-busy="true"
        aria-label={label}
        className={cn(loadingSkeletonVariants({ variant }), className)}
        {...props}
      >
        {content}
      </div>
    );
  },
);

LoadingSkeleton.displayName = 'LoadingSkeleton';

export { LoadingSkeleton, loadingSkeletonVariants };
